/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from "react"
import PropTypes from "prop-types"
import { Helmet } from 'react-helmet-async';
import { useStaticQuery, graphql } from "gatsby"

import shareCardImage from "../../images/share-card.png"
import { truncateAtWord } from "../../utils/text"
import {
  normalizeCanonicalPath,
  absoluteAssetUrl,
} from "../../utils/urls"

const META_TITLE_MAX_LENGTH = 60
const META_DESCRIPTION_MAX_LENGTH = 300

function SEO({ 
  description, 
  lang, 
  meta, 
  title, 
  dontIndex, 
  generateImage, 
  generateImageElement, 
  path, 
  imageUrl,
  imageSize,
  schema,
}) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            siteUrl
            author
          }
        }
      }
    `
  )

  const siteUrlRaw = site.siteMetadata.siteUrl || "https://thehiveindex.com"
  const baseUrl = siteUrlRaw.replace(/\/$/, "")

  var metaTitle = title
  if (metaTitle.length < 30 && !metaTitle.includes("Hive Index")) {
    metaTitle += " | Hive Index"
  }
  metaTitle = truncateAtWord(metaTitle, META_TITLE_MAX_LENGTH)

  const shareImageAlt = metaTitle

  let metaDescription
  const hasExplicitDescription =
    typeof description === "string" && description.trim().length > 0

  if (hasExplicitDescription) {
    metaDescription = description.trim()
  } else {
    metaDescription = site.siteMetadata.description || ""
    if (metaDescription.length < 90) {
      metaDescription +=
        " Browse communities today, or submit your own for free."
    }
  }
  metaDescription = truncateAtWord(
    metaDescription,
    META_DESCRIPTION_MAX_LENGTH
  )

  const canonicalPath = normalizeCanonicalPath(path)
  let pageUrl = canonicalPath ? `${baseUrl}${canonicalPath}` : ""
  if (!pageUrl && typeof window !== "undefined") {
    const currentPath =
      normalizeCanonicalPath(window.location.pathname) || "/"
    pageUrl = `${window.location.origin}${currentPath}`
  }

  var shareImageUrl = absoluteAssetUrl(baseUrl, shareCardImage)
  // Default share-card.png is 1200x600. Only declared when we know dimensions (default or apiflash); a custom imageUrl skips them.
  var shareImageDimensions = { width: 1200, height: 600 }
  if (imageUrl) {
    shareImageUrl = absoluteAssetUrl(baseUrl, imageUrl)
    shareImageDimensions = null
  }

  if (pageUrl && generateImage && process.env.NODE_ENV === "production") {
    shareImageUrl = `https://api.apiflash.com/v1/urltoimage?access_key=14d08832cb4347cfb2dd4598aea0985c&height=675&width=1200&ttl=1296000&response_type=image&format=png${generateImageElement ? '&element=' + encodeURIComponent(generateImageElement) : ''}&url=` + encodeURIComponent(pageUrl);
    shareImageDimensions = { width: 1200, height: 675 }
  }

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={metaTitle}
      titleTemplate={`%s`}
    >
      {pageUrl ? <link rel="canonical" href={pageUrl} /> : null}
      <meta name="description" content={metaDescription} />
      
      <meta name="title" content={metaTitle} />
      <meta property="og:title" content={metaTitle} />
      {pageUrl ? <meta property="og:url" content={pageUrl} /> : null}
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Hive Index" />
      <meta property="og:image" content={shareImageUrl} />
      <meta property="og:image:alt" content={shareImageAlt} />
      {shareImageDimensions ? (
        <meta property="og:image:width" content={String(shareImageDimensions.width)} />
      ) : null}
      {shareImageDimensions ? (
        <meta property="og:image:height" content={String(shareImageDimensions.height)} />
      ) : null}
      <meta property="og:locale" content="en_US" />
      
      {/* twitter */}
      {imageSize === "small" ? (
        <meta name="twitter:card" content="summary" />
      ) : (
        <meta name="twitter:card" content="summary_large_image" />
      )}
      {site.siteMetadata.author ? (
        <meta name="twitter:creator" content={site.siteMetadata.author} />
      ) : null}
      {site.siteMetadata.author ? (
        <meta name="twitter:site" content={site.siteMetadata.author} />
      ) : null}
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={shareImageUrl} />
      <meta name="twitter:image:alt" content={shareImageAlt} />
      <meta name="twitter:domain" content="thehiveindex.com" />
      {pageUrl ? <meta name="twitter:url" content={pageUrl} /> : null}

      {dontIndex ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {schema ? (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ) : ''}
    </Helmet>
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  path: PropTypes.string,
}

export default SEO
